import React, { PureComponent } from "react";

import {
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Line,
  LineChart,
  RadarChart,
  Radar,
  XAxis,
  YAxis,
  Tooltip
} from "recharts";

import { Dropdown, MenuItem, Popover, OverlayTrigger } from "react-bootstrap";

import { Button, ButtonGroup, Icon } from "aible";
import numeral from "numeral";

import FocusIcon from "images/Sense-Icons/arrows-to-circle-light.svg";
import EditChartIcon from "images/chart-edit.svg";

import LeakageChartContainer from "containers/Next/Charts/LeakageChart";
import NextChartContainer from "components/Next/Charts/NextChart";

import DashboardHeatmap from "components/Next/Charts/DashboardHeatmap";

import styles from "./style.module.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faMinus,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faExpandArrowsAlt,
  faSortNumericDown,
  faSortAlphaDown
} from "@fortawesome/free-solid-svg-icons";

import { rgb } from "d3-color";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/aible/Dialog/Dialog";

function parseNumericLabel(label) {
  if (!isNaN(label)) {
    return Number(label);
  }
  const labelArr = label.replace("<", "").replace(">", "").split(" to ");
  let retLabel = labelArr[labelArr.length - 1];

  if (retLabel === "inf" || retLabel === "Infinity" || retLabel === "infinity") {
    return Infinity;
  }
  if (retLabel === "-inf" || retLabel === "-Infinity" || retLabel === "-infinity") {
    return -Infinity;
  }
  if (isNaN(retLabel)) {
    return label;
  }

  return Number(retLabel);
}

const CustomizedDot = (props) => {
  const { cx, cy, stroke, payload, value, sIndex, isOutline, hoverLegend, isCustomTooltip, dataKey } = props;

  let width =
    props.cWidth !== undefined ? props.cWidth : hoverLegend === undefined ? 8 : hoverLegend === dataKey ? 12 : 4;
  let height =
    props.cHeight !== undefined ? props.cHeight : hoverLegend === undefined ? 8 : hoverLegend === dataKey ? 12 : 4;

  const OUTLINE_SCALE = 0.5;
  const LARGE_SCALE = 1.6;

  if (isOutline) {
    width *= OUTLINE_SCALE;
    height *= OUTLINE_SCALE;
  }

  // SQUARE
  if (sIndex === 0) {
    return (
      <svg
        x={isCustomTooltip ? 0 : cx - width / 2}
        y={isCustomTooltip ? 0 : cy - height / 2}
        width={width}
        height={height}
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="14" height="14" fill={stroke} />
      </svg>
    );
    // CIRCLE
  } else if (sIndex === 1) {
    return (
      <svg
        x={isCustomTooltip ? 0 : cx - width / 2}
        y={isCustomTooltip ? 0 : cy - height / 2}
        width={width}
        height={height}
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="7" cy="7" r="7" fill={stroke} />
      </svg>
    );
    // DIAMOND
  } else if (sIndex === 2) {
    height *= LARGE_SCALE;
    width *= LARGE_SCALE;
    return (
      <svg
        x={isCustomTooltip ? 0 : cx - width / 2}
        y={isCustomTooltip ? 0 : cy - height / 2}
        width={width}
        height={height}
        viewBox="0 0 13 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M0.661182 9L6.55026 0.550262L12.4393 9L6.55026 17.4497L0.661182 9Z" fill={stroke} />
      </svg>
    );
  } // STAR
  else if (sIndex === 3) {
    height *= LARGE_SCALE;
    width *= LARGE_SCALE;
    return (
      <svg
        x={isCustomTooltip ? 0 : cx - width / 2}
        y={isCustomTooltip ? 0 : cy - height / 2}
        width={width}
        height={height}
        viewBox="0 0 18 17"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9 0L11.7508 5.2138L17.5595 6.21885L13.4509 10.4462L14.2901 16.2812L9 13.68L3.70993 16.2812L4.54906 10.4462L0.440492 6.21885L6.24917 5.2138L9 0Z"
          fill={stroke}
        />
      </svg>
    );
  } // TRIANGLE?
  else if (sIndex === 4) {
    height *= LARGE_SCALE;
    width *= LARGE_SCALE;
    return (
      <svg
        x={isCustomTooltip ? 0 : cx - width / 2}
        y={isCustomTooltip ? 0 : cy - height / 2}
        width={width}
        height={height}
        viewBox="0 0 17 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M8.5 0L16.7272 14.25H0.272758L8.5 0Z" fill={stroke} />
      </svg>
    );
  }
  return (
    <svg
      x={isCustomTooltip ? 0 : cx - width / 2}
      y={isCustomTooltip ? 0 : cy - height / 2}
      width={width}
      height={height}
      viewBox="0 0 18 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9 0L11.7508 5.2138L17.5595 6.21885L13.4509 10.4462L14.2901 16.2812L9 13.68L3.70993 16.2812L4.54906 10.4462L0.440492 6.21885L6.24917 5.2138L9 0Z"
        fill={stroke}
      />
    </svg>
  );
};

const CustomTooltip = ({ active, payload, label, isFix }) => {
  if (active && payload && payload.length) {
    const column = payload?.[0]?.payload?.column;
    const count = payload?.[0]?.payload?.count;
    return (
      <div className={styles.tooltipContent}>
        <div className={styles.tLabel} style={{ display: "flex", whiteSpace: "pre" }}>
          {column !== undefined ? (
            <React.Fragment>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", flexShrink: "1" }}>{column}</span>
              <span>{" is "}</span>
              <span>{label}</span>
            </React.Fragment>
          ) : (
            label
          )}
        </div>
        {count !== undefined && <div className={styles.tLabel}>{"Count: " + count}</div>}
        {payload.map((iPayload, i) => (
          <p
            key={"tt-" + i}
            className={isFix ? styles.cInnerItemFix : styles.cInnerItem}
            style={{ color: iPayload?.color }}
          >
            <span className={styles.cInnerDot}>
              <CustomizedDot sIndex={i} cWidth={12} cHeight={12} isCustomTooltip />
            </span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", flexShrink: "1" }}>{iPayload.name}</span>
            <span>{": "}</span>
            <span>{iPayload.value}</span>
          </p>
        ))}
      </div>
    );
  }

  return null;
};

const yAxisTickFormat = (tickItem) => {
  if (!isNaN(tickItem)) {
    return numeral(tickItem).format("0.[0]a");
  }
  if (String(tickItem).length > 15) {
    return String(tickItem).substring(0, 10) + "...";
  }
  return tickItem;
};

export default class DashboardChart extends PureComponent {
  constructor(props) {
    super(props);

    this.myRef = React.createRef();

    this.state = {
      isHighlight: undefined,
      secondaryChartIndex: 0,
      expandChart: undefined,
      hoverLegend: undefined,
      hoverTimeout: undefined,
      sChartIndex: 0,
      maxLines: 3,
      showSecondary: false
    };
  }

  getIsContinuous() {
    const { dataset } = this.props;

    const outcome = dataset?.outcome;
    const oField = dataset?.fields?.[outcome];

    return oField?.type === "number";
  }

  renderTinyScore(node) {
    const { id, isRight, chart } = this.props;

    return (
      <div className={styles.tinyMuscleScoreDash}>
        <LeakageChartContainer
          id={id}
          height={110}
          width={110}
          innerRadius={13}
          node={isRight ? chart?.firstChild : node}
          insight={isRight ? chart?.firstChild : node}
          isTiny
          hideChords={isRight !== true}
          isDashboard
        />
        {isRight === true ? (
          <div />
        ) : (
          <div className={styles.tinyScore} style={node?.rank + 1 >= 10 ? { marginLeft: "-4px" } : {}}>
            {node?.rank + 1}
          </div>
        )}
      </div>
    );
  }

  getLegendTypeFromIndex(index) {
    const LEGEND_TYPES = ["square", "circle", "diamond", "star", "triangle", "wye"];

    return LEGEND_TYPES?.[index] || LEGEND_TYPES?.[0];
  }

  render() {
    const { isInfographic } = this.props;

    return isInfographic ? this.renderInfographic() : this.renderDashboard();
  }

  renderInfographic() {
    const {
      id,
      chart,
      isHideLegend,
      isAngleAxis,
      updateSearchDashboard,
      updateSearchDashboardSimple,
      quickSearch,
      isDomainMin,
      updateHideLines,
      dashboardChartOptions,
      updateDashboardChartOptions,
      hideLines,
      hideSecondary,
      uChartStr,
      isRight,
      dataset,
      openModal
    } = this.props;

    const {
      secondaryChartIndex,
      hoverLegend,
      sChartIndex,
      maxLines,
      forceFilter,
      showSecondary,
      isHighlight,
      hoverTimeout
    } = this.state;

    const isContinuous = this.getIsContinuous();
    const isNumeric = chart?.mainChart?.isNumeric;

    const retHoverLegend = forceFilter !== undefined ? forceFilter : hoverLegend;

    const lineKeys =
      chart?.data?.[0] === undefined
        ? []
        : Object.keys(chart.data[0]).filter(
            (cKey) =>
              cKey !== "count" &&
              cKey !== "name" &&
              cKey !== "column" &&
              cKey !== "min" &&
              cKey !== "max" &&
              hideLines?.[cKey] !== true
          );
    const detailLineKeys =
      chart?.detailData?.[0] === undefined
        ? []
        : Object.keys(chart.detailData[0]).filter(
            (cKey) =>
              cKey !== "count" &&
              cKey !== "name" &&
              cKey !== "column" &&
              cKey !== "min" &&
              cKey !== "max" &&
              hideLines?.[cKey] !== true &&
              (isContinuous ? cKey !== `Average ${chart.detailData[0].column}` : true)
          );

    const longestName = (chart?.data || []).reduce(
      (a, b) => Math.max(a, b.name !== undefined ? b.name.toString().length : 0),
      0
    );

    const renderColorfulLegendText = (value, entry) => {
      const { color } = entry;

      return (
        <div
          className={
            styles.customLegendDashboard + " " + (hideLines?.[value] === true ? styles.customLegendHidden : "")
          }
          style={{
            textAlign: "left",
            opacity: retHoverLegend === undefined || retHoverLegend === value || isStackedBar !== true ? 1 : 0.6
          }}
        >
          {chart?.topText?.[value] ? (
            <OverlayTrigger
              placement="top"
              trigger={["hover"]}
              rootClose
              overlay={
                <Popover id="popover-positioned-top" style={{ pointerEvents: "none", width: "250px" }}>
                  <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{chart?.topText?.[value]}</div>
                </Popover>
              }
            >
              <div className={styles.customLegendLabel}>{value}</div>
            </OverlayTrigger>
          ) : (
            <div className={styles.customLegendLabel}>{value}</div>
          )}
          <div className={styles.legendActions}>
            <ButtonGroup>
              <OverlayTrigger
                placement="top"
                trigger={["hover"]}
                rootClose
                overlay={
                  <Popover id="popover-positioned-top" style={{ pointerEvents: "none" }}>
                    <div>{"Focus"}</div>
                  </Popover>
                }
              >
                <Button
                  active={forceFilter === value}
                  onClick={(e) => {
                    e.stopPropagation();
                    this.setState({
                      forceFilter: forceFilter === value ? undefined : value,
                      sChartIndex: 0
                    });
                  }}
                  style={{ padding: "3px" }}
                >
                  <Icon src={FocusIcon} size={16} />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                trigger={["hover"]}
                rootClose
                overlay={
                  <Popover id="popover-positioned-top" style={{ pointerEvents: "none" }}>
                    <div>{"Search: " + value}</div>
                  </Popover>
                }
              >
                <Button
                  onClick={(e) => {
                    updateSearchDashboardSimple(value);
                  }}
                  style={{ padding: "3px" }}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </Button>
              </OverlayTrigger>
            </ButtonGroup>
          </div>
        </div>
      );
    };

    const nChart = chart?.secondaryCharts?.[secondaryChartIndex];

    const aWidth = 1920;
    const myWidth = window?.innerWidth || aWidth;
    const xPos = (myWidth / 10 + 141) * (myWidth / aWidth);

    const retColors = isRight ? chart?.detailColors : chart?.colors;
    const retKeys = isRight ? detailLineKeys : lineKeys;

    const fColumn = chart?.firstChild?.metadata?.properties?.[0]?.column;
    const sColumn = chart?.firstChild?.metadata?.properties?.[1]?.column;

    const fColumnType = dataset?.fields?.[fColumn]?.type;
    const sColumnType = dataset?.fields?.[sColumn]?.type;

    const mColumn = chart?.mainChart?.metadata?.properties?.[0]?.column;

    const sortType = this.state.sortType ?? (isContinuous ? "ALPHABETIC" : "COUNT");

    let shouldSortData = dataset?.fields?.[mColumn]?.type !== "date";

    const rawData = isRight ? chart?.detailData : chart?.data;
    const retData = !shouldSortData
      ? rawData
      : [...rawData].sort((a, b) => {
          if (sortType === "COUNT") {
            const aCount = a?.count || 0;
            const bCount = b?.count || 0;
            if (aCount < bCount) {
              return 1;
            } else if (aCount > bCount) {
              return -1;
            }
          }

          let aLabel = a?.name;
          let bLabel = b?.name;

          if (!isNaN(aLabel) && !isNaN(bLabel)) {
            aLabel = Number(aLabel);
            bLabel = Number(bLabel);
          } else if (isNumeric) {
            aLabel = parseNumericLabel(aLabel);
            bLabel = parseNumericLabel(bLabel);
          }

          if (aLabel < bLabel) {
            return -1;
          } else if (aLabel > bLabel) {
            return 1;
          } else {
            return 0;
          }
        });

    const chartOptions = dashboardChartOptions?.[uChartStr] || {
      type: isRight
        ? fColumnType === "number" && sColumnType === "number"
          ? "Heatmap"
          : retData && retData.length >= 10
            ? "Grouped Bar Chart"
            : retData && retData.length >= 3
              ? "Grouped Bar Chart (Horizontal)"
              : "Heatmap"
        : isNumeric
          ? "Line"
          : retData && retData.length >= 10
            ? "Grouped Bar Chart"
            : "Grouped Bar Chart (Horizontal)"
    };

    const isHeatmap =
      chartOptions?.type === "Heatmap"
        ? true
        : chartOptions?.type === "Line" ||
            chartOptions?.type === "Spline" ||
            chartOptions?.type === "Grouped Bar Chart (Horizontal)" ||
            chartOptions?.type === "Grouped Bar Chart" ||
            chartOptions?.type === "Radar Chart"
          ? false
          : Boolean(chart?.detailData && chart.detailData.length > 3);

    const isRadar = isRight !== true ? false : chartOptions?.type === "Radar Chart";

    const isStackedBar =
      chartOptions?.type === "Grouped Bar Chart" || chartOptions?.type === "Grouped Bar Chart (Horizontal)"
        ? true
        : isRight === true && chartOptions?.type !== "Spline" && chartOptions?.type !== "Line";

    const retSecondaryCharts =
      chart?.secondaryCharts &&
      chart?.secondaryCharts.filter((nChart, i) =>
        retHoverLegend === undefined || retHoverLegend.indexOf("Average") === 0
          ? true
          : nChart?.metadata?.properties?.[0]?.column === retHoverLegend.split(" is ")?.[0]
      );

    const toolPos = document.getElementById("tooltipPosition_" + chart?.title)
      ? document.getElementById("tooltipPosition_" + chart?.title).getBoundingClientRect()
      : undefined;

    const chartOptionsSelect = [
      { label: "Grouped Bar Chart (Horizontal)", value: "Grouped Bar Chart (Horizontal)" },
      { label: "Grouped Bar Chart (Vertical)", value: "Grouped Bar Chart" },
      { label: "Line", value: "Line" },
      { label: "Spline", value: "Spline" },
      { label: "Heatmap", value: "Heatmap" }
    ];

    if (isRight) {
      chartOptionsSelect.unshift({ label: "Radar Chart", value: "Radar Chart" });
    }

    const categorySize = (retData ?? [])?.length;
    const totalBars = categorySize * maxLines;
    const hasLargeBarData = chartOptions?.type === "Grouped Bar Chart (Horizontal)" ? totalBars >= 20 : totalBars >= 60;
    const hoverTimoutInterval = hasLargeBarData ? 15 : 5;

    return (
      <div id={"dashboardChart"} className={styles.dashboardChart}>
        {this.renderExpandModal()}
        <div
          className={styles.dashboardChartTitle}
          style={{ borderBottom: "none" }}
          onClick={() => quickSearch(chart?.title)}
        >
          {this.renderTinyScore(chart?.mainChart)}
          {isRight ? (
            <span className={styles.chartTitleEllipsis}>{chart?.rightTitle}</span>
          ) : (
            <div style={{ display: "flex", whiteSpace: "pre", maxWidth: "calc(100% - 50px)" }}>
              <span>{"Average "}</span>
              <span className={styles.chartTitleEllipsis}>{dataset?.outcome}</span>
              <span>{" by "}</span>
              <span className={styles.chartTitleEllipsis}>{chart?.title}</span>
            </div>
          )}
        </div>
        <div
          className={styles.infoChartInner}
          style={{ height: hideSecondary ? "calc(100% - 20px)" : showSecondary ? "calc(60% - 40px)" : undefined }}
        >
          <div
            style={{
              height: "100%",
              width: "100%",
              zIndex: 10,
              position: "relative"
            }}
          >
            <div className={styles.infoChartOptions}>
              <div className={styles.infoChartActions}>
                {isHeatmap !== true && (
                  <div className={styles.linesToggle}>
                    <div style={{ marginRight: "4px", paddingTop: "2px" }}>
                      {Math.min(maxLines, retKeys.length) +
                        "/" +
                        retKeys.length +
                        (maxLines - 1 === 1 ? " Insight" : " Insights")}
                    </div>
                    <ButtonGroup>
                      <Button
                        style={{ padding: "3px" }}
                        disabled={maxLines <= 1}
                        onClick={() => this.setState({ maxLines: maxLines - 1 })}
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </Button>
                      <Button
                        style={{ padding: "3px" }}
                        disabled={maxLines > retKeys.length - 1}
                        onClick={() => this.setState({ maxLines: maxLines + 1 })}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </Button>
                    </ButtonGroup>
                  </div>
                )}
                {!isRight && (
                  <ButtonGroup style={{ marginRight: "10px", marginLeft: "10px" }}>
                    {!isHeatmap && (
                      <OverlayTrigger
                        placement="top"
                        trigger={["hover"]}
                        rootClose
                        overlay={
                          <Popover id="popover-positioned-top">
                            <div>{"Sort by Count"}</div>
                          </Popover>
                        }
                      >
                        <Button
                          style={{ padding: "7px 10px" }}
                          onClick={() => {
                            this.setState({
                              sortType: "COUNT"
                            });
                          }}
                          active={sortType === "COUNT"}
                          title="Toggle Sort"
                          greyscaleDefaultState
                        >
                          <div className={styles.chartActionsIcon}>
                            <FontAwesomeIcon icon={faSortNumericDown} />
                          </div>
                        </Button>
                      </OverlayTrigger>
                    )}
                    <OverlayTrigger
                      placement="top"
                      trigger={["hover"]}
                      rootClose
                      overlay={
                        <Popover id="popover-positioned-top">
                          <div>{"Sort by Label"}</div>
                        </Popover>
                      }
                    >
                      <Button
                        style={{ padding: "7px 10px" }}
                        onClick={() => {
                          this.setState({
                            sortType: "ALPHABETIC"
                          });
                        }}
                        active={sortType === "ALPHABETIC"}
                        title="Toggle Sort"
                        greyscaleDefaultState
                      >
                        <div className={styles.chartActionsIcon}>
                          <FontAwesomeIcon icon={faSortAlphaDown} />
                        </div>
                      </Button>
                    </OverlayTrigger>
                  </ButtonGroup>
                )}
                {openModal !== undefined && (
                  <Button
                    onClick={() => openModal()}
                    style={{ padding: "7px 10px", display: "inline-flex" }}
                    greyscaleDefaultState
                  >
                    <div className={styles.chartActionsIcon}>
                      <FontAwesomeIcon icon={faExpandArrowsAlt} />
                    </div>
                  </Button>
                )}
              </div>
              <div className={styles.infoChartEdit}>
                <div id={"tooltipPosition_" + chart?.title} />
                {hideLines && Object.keys(hideLines).length > 0 && (
                  <div className={styles.hiddenLines} onClick={() => updateHideLines({})}>
                    <a>{"Show hidden"}</a>
                  </div>
                )}
                <div className={isHeatmap ? styles.infoEditHeatmap : styles.infoEditChart}>
                  <EditChartButton
                    updateDashboardChartOptions={updateDashboardChartOptions}
                    dashboardChartOptions={dashboardChartOptions}
                    chartTitleKey={uChartStr}
                    chartOptions={chartOptionsSelect}
                  />
                </div>
              </div>
            </div>
            {isHeatmap ? (
              <div className={styles.heatmapHolder}>
                <DashboardHeatmap chart={{ data: retData, lineKeys: retKeys }} dataset={dataset} />
              </div>
            ) : isRadar ? (
              <div className={styles.radarHolder}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart
                    data={retData || []}
                    onClick={(gItem) => updateSearchDashboard(gItem)}
                    onMouseMove={(e) => {
                      if (e?.activeTooltipIndex !== undefined && this.state.isHighlight !== e?.activeTooltipIndex) {
                        this.setState({ isHighlight: e.activeTooltipIndex });
                      } else {
                        this.setState({
                          isHighlight: e?.activeLabel ? e?.activeLabel : undefined
                        });
                      }
                    }}
                    onMouseLeave={() => this.setState({ isHighlight: undefined })}
                    cx="50%"
                    cy="50%"
                    outerRadius="60%"
                  >
                    <PolarGrid />
                    <PolarAngleAxis dataKey="name" />
                    <PolarRadiusAxis angle={150} domain={isDomainMin ? ["dataMin", "dataMax"] : undefined} />
                    <Tooltip
                      position={{
                        x: nChart === undefined ? xPos + 390 : xPos + 140,
                        y: 70
                      }}
                      wrapperStyle={{
                        width: "220px",
                        background: "white",
                        padding: "10px",
                        paddingBottom: "0px",
                        border: "1px solid rgba(0, 0, 0, 0.2)",
                        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)"
                      }}
                      contentStyle={{
                        background: "white",
                        border: "none"
                      }}
                      content={<CustomTooltip isFix />}
                      active={this.state.isHighlight !== undefined}
                    />
                    {isHideLegend !== true && (
                      <Legend
                        align={"center"}
                        onMouseMove={(legItem) => this.setState({ hoverLegend: legItem?.value })}
                        onMouseLeave={() => this.setState({ hoverLegend: undefined })}
                        formatter={renderColorfulLegendText}
                      />
                    )}
                    {detailLineKeys
                      .filter((lineKey, sIndex) => sIndex < maxLines)
                      .map((lineKey, sIndex) => (
                        <Radar
                          dataKey={lineKey}
                          stroke={
                            retHoverLegend === undefined || retHoverLegend === lineKey
                              ? retColors[sIndex]
                              : rgb(retColors[sIndex]).copy({ opacity: 0.6 })
                          }
                          fill={retColors[sIndex]}
                          fillOpacity={retHoverLegend === undefined ? 0.6 : retHoverLegend === lineKey ? 1 : 0.2}
                          key={"radar_" + sIndex}
                        />
                      ))}
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            ) : isStackedBar ? (
              <div className={styles.stackedBarHolder}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={retData || []}
                    onClick={(gItem) => updateSearchDashboard(gItem)}
                    onMouseMove={(e) => {
                      if (e?.activeTooltipIndex !== undefined && this.state.isHighlight !== e?.activeTooltipIndex) {
                        if (hoverTimeout) {
                          clearTimeout(hoverTimeout);
                          this.setState({ hoverTimeout: undefined });
                        }
                        const newTimeout = setTimeout(() => {
                          this.setState({ isHighlight: e.activeTooltipIndex });
                        }, hoverTimoutInterval);
                        this.setState({ hoverTimeout: newTimeout });
                      }
                    }}
                    onMouseLeave={() => {
                      if (hoverTimeout) {
                        clearTimeout(hoverTimeout);
                        this.setState({ hoverTimeout: undefined });
                      }
                      this.setState({ isHighlight: undefined });
                    }}
                    barGap={hasLargeBarData ? 0 : chartOptions?.type === "Grouped Bar Chart (Horizontal)" ? 2 : 6}
                    barCategoryGap={
                      hasLargeBarData ? 2 : chartOptions?.type === "Grouped Bar Chart (Horizontal)" ? `15%` : `20%`
                    }
                    margin={
                      chartOptions?.type === "Grouped Bar Chart (Horizontal)"
                        ? { right: 20, left: isContinuous ? 40 : 40 }
                        : { right: 20, left: isContinuous ? 40 : undefined }
                    }
                    layout={chartOptions?.type === "Grouped Bar Chart (Horizontal)" ? "vertical" : "horizontal"}
                  >
                    <CartesianGrid strokeDasharray={"3 3"} strokeOpacity={".7"} />
                    {chartOptions?.type === "Grouped Bar Chart (Horizontal)" ? (
                      <React.Fragment>
                        <XAxis domain={isDomainMin ? ["dataMin", "dataMax"] : undefined} width={30} type="number" />
                        <YAxis dataKey="name" type="category" tick={<CustomEllipsisLabel yAxis={true} />} />
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <XAxis dataKey="name" tick={<CustomEllipsisLabel />} />
                        <YAxis domain={isDomainMin ? ["dataMin", "dataMax"] : undefined} width={30} />
                      </React.Fragment>
                    )}
                    <Tooltip
                      position={{
                        x: nChart === undefined ? xPos + 390 : xPos + 60,
                        y: 0
                      }}
                      wrapperStyle={{
                        width: "220px",
                        background: "white",
                        padding: "10px",
                        paddingBottom: "0px",
                        border: "1px solid rgba(0, 0, 0, 0.2)",
                        boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)"
                      }}
                      contentStyle={{
                        background: "white",
                        border: "none"
                      }}
                      content={<CustomTooltip isFix />}
                      active={this.state.isHighlight !== undefined}
                      cursor={{ fill: "#EFECF6", opacity: ".8" }}
                    />
                    {isHideLegend !== true && (
                      <Legend
                        align={"center"}
                        onMouseMove={(legItem) => this.setState({ hoverLegend: legItem?.value })}
                        onMouseLeave={() => this.setState({ hoverLegend: undefined })}
                        formatter={renderColorfulLegendText}
                      />
                    )}
                    {retKeys
                      .filter((lineKey, sIndex) => sIndex < maxLines)
                      .map((lineKey, sIndex) => {
                        return (
                          <Bar
                            dataKey={lineKey}
                            stroke={
                              hasLargeBarData
                                ? undefined
                                : retHoverLegend === undefined || retHoverLegend === lineKey
                                  ? retColors[sIndex]
                                  : rgb(retColors[sIndex]).copy({ opacity: 0.6 })
                            }
                            fill={retColors[sIndex]}
                            key={"bar_" + sIndex}
                          >
                            {(retData || []).map((entry, index) => (
                              <Cell
                                fillOpacity={
                                  isHighlight !== undefined
                                    ? isHighlight === index
                                      ? 1
                                      : hasLargeBarData
                                        ? 0.3
                                        : 0.2
                                    : retHoverLegend === lineKey || retHoverLegend === undefined
                                      ? 1
                                      : hasLargeBarData
                                        ? 0.3
                                        : 0.2
                                }
                              />
                            ))}
                          </Bar>
                        );
                      })}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  width={500}
                  height={260}
                  data={retData || []}
                  margin={{
                    top: 5,
                    right: 40,
                    left: isContinuous ? 40 : 0,
                    bottom: isRight ? 20 : 0
                  }}
                  onClick={(gItem) => updateSearchDashboard(gItem)}
                  onMouseMove={(e) => {
                    if (e?.activeTooltipIndex !== undefined && this.state.isHighlight !== e?.activeTooltipIndex) {
                      this.setState({ isHighlight: e.activeTooltipIndex });
                    } else {
                      this.setState({
                        isHighlight: e?.activeLabel ? e?.activeLabel : undefined
                      });
                    }
                  }}
                  onMouseLeave={() => this.setState({ isHighlight: undefined })}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  {isAngleAxis !== true ? (
                    <XAxis
                      dataKey="name"
                      interval={"preserveStartEnd"}
                      onClick={(aItem) => updateSearchDashboard(aItem)}
                      tick={<CustomEllipsisLabel />}
                    />
                  ) : (
                    <XAxis
                      dataKey="name"
                      interval={"preserveStartEnd"}
                      height={40 + longestName * 4.5}
                      onClick={(aItem) => updateSearchDashboard(aItem)}
                    />
                  )}
                  <YAxis domain={isDomainMin ? ["dataMin", "dataMax"] : undefined} width={30} />
                  <Tooltip
                    position={{
                      x: isRight ? xPos + 110 : toolPos ? toolPos.left - 150 : 950,
                      y: isRight ? 34 : 20
                    }}
                    wrapperStyle={{
                      width: "220px",
                      background: "white",
                      padding: "10px",
                      paddingBottom: "0px",
                      border: "1px solid rgba(0, 0, 0, 0.2)",
                      boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)"
                    }}
                    contentStyle={{
                      background: "white",
                      border: "none"
                    }}
                    content={<CustomTooltip isFix />}
                    active={this.state.isHighlight !== undefined}
                  />
                  {isHideLegend !== true && (
                    <Legend
                      align="center"
                      onMouseMove={(legItem) => this.setState({ hoverLegend: legItem?.value })}
                      onMouseLeave={() => this.setState({ hoverLegend: undefined })}
                      formatter={renderColorfulLegendText}
                    />
                  )}
                  {retKeys
                    .filter((lineKey, sIndex) => sIndex < maxLines)
                    .map((lineKey, sIndex) => (
                      <Line
                        type={chartOptions?.type === "Spline" ? "monotone" : "linear"}
                        dataKey={lineKey}
                        stroke={
                          retHoverLegend === undefined || retHoverLegend === lineKey
                            ? retColors[sIndex]
                            : rgb(retColors[sIndex]).copy({ opacity: 0.3 })
                        }
                        strokeWidth={retHoverLegend === undefined ? 3 : retHoverLegend === lineKey ? 3 : 1}
                        legendType={this.getLegendTypeFromIndex(sIndex)}
                        dot={<CustomizedDot sIndex={sIndex} retHoverLegend={retHoverLegend} />}
                        activeDot={<CustomizedDot sIndex={sIndex} retHoverLegend={retHoverLegend} isOutline />}
                        key={"line_" + sIndex}
                      />
                    ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        {hideSecondary !== true &&
          (showSecondary ? (
            <React.Fragment>
              <div className={styles.secondaryInsightInfo}>
                {forceFilter && (
                  <div className={styles.filterBadge}>
                    <div className={styles.filterText}>{forceFilter}</div>
                    <div className={styles.filterBadgeIcon} onClick={() => this.setState({ forceFilter: undefined })}>
                      {"X"}
                    </div>
                  </div>
                )}
                <div>
                  {"(" +
                    Math.min(sChartIndex * 4 + 4, retSecondaryCharts.length) +
                    "/" +
                    retSecondaryCharts.length +
                    ")" +
                    " areas where " +
                    chart?.title +
                    " behaves differently"}
                </div>
                <a
                  className={styles.secondaryInsightLink}
                  style={{ marginLeft: "20px" }}
                  onClick={() => {
                    this.setState({ showSecondary: !this.state.showSecondary });
                  }}
                >
                  {`Hide`}
                </a>
              </div>
              <div className={styles.sBotCharts}>
                <Button
                  disabled={sChartIndex <= 0}
                  onClick={() => this.setState({ sChartIndex: sChartIndex - 1 })}
                  style={{ border: "none" }}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Button>
                {retSecondaryCharts &&
                  retSecondaryCharts
                    .filter((nChart, i) =>
                      hoverLegend !== undefined && hoverLegend.indexOf("Average") !== 0
                        ? true
                        : i <= sChartIndex * 4 + 3 && i >= sChartIndex * 4
                    )
                    .filter((nChart, i) => i < 4)
                    .map((nChart, cIndex) => (
                      <div className={styles.sChartTile} key={"sChart_" + cIndex}>
                        <NextChartContainer
                          id={id}
                          // TODO: Madness please fix this. To show muscle chart
                          node={{
                            chart: {
                              chart: nChart,
                              fields: nChart.fields,
                              narrative: nChart?.narrative
                            },
                            fields: nChart.fields
                          }}
                          chart={{ ...nChart, chart: nChart.chart }}
                          isSmall={true}
                          hidePin={true}
                          isListChart={true}
                          showTooltip={true}
                          showLeakage={true}
                          showAxis={false}
                          isDashboard={true}
                          hideLegend={true}
                          showTitle={true}
                          isInfographic={true}
                          isExpandChart={this.state.expandChart !== undefined}
                          updateIsExpandChart={(rChart) => this.setState({ expandChart: rChart })}
                          tooltipPosition={{
                            x: 0,
                            y: -400
                          }}
                          useNode
                        />
                      </div>
                    ))}
                <Button
                  disabled={(sChartIndex + 1) * 4 >= retSecondaryCharts.length}
                  onClick={() => this.setState({ sChartIndex: sChartIndex + 1 })}
                  style={{ border: "none" }}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              </div>
            </React.Fragment>
          ) : (
            <div className={styles.secondaryInsightInfo}>
              <a
                className={styles.secondaryInsightLink}
                onClick={() => {
                  this.setState({ showSecondary: !this.state.showSecondary });
                }}
              >
                {`Show areas where ${chart?.title} behaves differently`}
              </a>
            </div>
          ))}
      </div>
    );
  }

  renderDashboard() {
    const {
      id,
      chart,
      isHideLegend,
      isAngleAxis,
      updateSearchDashboard,
      updateSearchDashboardSimple,
      quickSearch,
      removeOrAddNode,
      isDomainMin,
      isInReport,
      updateHideLine,
      updateHideLines,
      dashboardChartOptions,
      updateDashboardChartOptions,
      hideLines,
      isInfographic
    } = this.props;

    const { secondaryChartIndex, hoverLegend, isHoverNextChart, isHighlight } = this.state;

    const lineKeys =
      chart?.data?.[0] === undefined
        ? []
        : Object.keys(chart.data[0]).filter(
            (cKey) =>
              cKey !== "count" &&
              cKey !== "name" &&
              cKey !== "column" &&
              cKey !== "min" &&
              cKey !== "max" &&
              hideLines?.[cKey] !== true
          );

    const longestName = (chart?.data || []).reduce(
      (a, b) => Math.max(a, b.name !== undefined ? b.name.toString().length : 0),
      0
    );

    const renderColorfulLegendText = (value, entry) => {
      const { color } = entry;

      return (
        <div className={styles.customLegendDashboard} style={{ textAlign: "left" }}>
          <div className={styles.customLegendLabel}>{value}</div>
          <div className={styles.legendActions}>
            <ButtonGroup>
              <OverlayTrigger
                placement="top"
                trigger={["hover"]}
                rootClose
                overlay={
                  <Popover id="popover-positioned-top" style={{ pointerEvents: "none" }}>
                    <div>{"Search: " + value}</div>
                  </Popover>
                }
              >
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateSearchDashboardSimple(value);
                  }}
                  style={{ padding: "3px" }}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </Button>
              </OverlayTrigger>
            </ButtonGroup>
          </div>
        </div>
      );
    };

    const nChart = chart?.secondaryCharts?.[secondaryChartIndex];

    const aWidth = 1920;
    const myWidth = window?.innerWidth || aWidth;
    const xPos = (myWidth / 10 + 141) * (myWidth / aWidth);

    const chartOptions = dashboardChartOptions?.[chart?.title] || {};

    const editChartOptions = [
      { label: "Line", value: "Line" },
      { label: "Spline", value: "Spline" }
    ];

    return (
      <div id={"dashboardChart"} className={styles.dashboardChart}>
        {this.renderExpandModal()}
        <div
          className={styles.dashboardChartTitleLayout}
          style={
            isInfographic ? { borderBottom: "none" } : { background: rgb(chart?.colors?.[0]).copy({ opacity: 0.1 }) }
          }
          onClick={() => quickSearch(chart?.title)}
        >
          {this.renderTinyScore(chart?.mainChart)}
          {chart?.title}
          <div className={styles.secondaryInsight}>
            {chart?.secondaryCharts?.length + " areas where " + chart?.title + " behaves differently"}
          </div>
        </div>
        <div className={styles.dashboardChartInner}>
          <div
            style={{
              height: "100%",
              width: nChart !== undefined ? "66.66%" : "100%",
              zIndex: 10,
              position: "relative"
            }}
          >
            <EditChartButton
              updateDashboardChartOptions={updateDashboardChartOptions}
              dashboardChartOptions={dashboardChartOptions}
              chartTitleKey={chart?.title}
              chartOptions={editChartOptions}
            />
            {hideLines && Object.keys(hideLines).length > 0 && (
              <div className={styles.hideLinesShowAll} onClick={() => updateHideLines({})}>
                <a>{"Show all"}</a>
              </div>
            )}
            {chartOptions?.type === "Radar Chart" ? (
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart
                  width={400}
                  height={300}
                  data={chart?.data || []}
                  margin={{
                    top: 5,
                    right: 220,
                    left: 20,
                    bottom: 0
                  }}
                  onClick={(gItem) => updateSearchDashboard(gItem)}
                  onMouseMove={(e) => {
                    if (e?.activeTooltipIndex !== undefined && this.state.isHighlight !== e?.activeTooltipIndex) {
                      this.setState({ isHighlight: e.activeTooltipIndex });
                    } else {
                      this.setState({
                        isHighlight: e?.activeLabel ? e?.activeLabel : undefined
                      });
                    }
                  }}
                  onMouseLeave={() => this.setState({ isHighlight: undefined })}
                >
                  <PolarGrid />
                  <PolarAngleAxis dataKey="name" />
                  <PolarRadiusAxis angle={30} domain={isDomainMin ? ["dataMin", "dataMax"] : undefined} />
                  <Tooltip
                    position={{
                      x: nChart === undefined ? xPos + 390 : xPos + 76,
                      y: 24
                    }}
                    wrapperStyle={{
                      background: "white",
                      padding: "10px",
                      border: "none"
                    }}
                    contentStyle={{
                      background: "white",
                      border: "none"
                    }}
                    content={<CustomTooltip />}
                    active={this.state.isHighlight !== undefined}
                  />
                  {isHideLegend !== true && (
                    <Legend
                      height={100}
                      width={200}
                      wrapperStyle={{
                        right: 0,
                        textAlign: "left"
                      }}
                      align="right"
                      verticalAlign="middle"
                      onMouseMove={(legItem) => this.setState({ hoverLegend: legItem?.value })}
                      onMouseLeave={() => this.setState({ hoverLegend: undefined })}
                      formatter={renderColorfulLegendText}
                    />
                  )}
                  {lineKeys.map((lineKey, sIndex) => (
                    <Radar
                      key={"r" + sIndex}
                      dataKey={lineKey}
                      stroke={chart?.colors[sIndex]}
                      strokeWidth={1}
                      fillOpacity={0.6}
                      legendType={this.getLegendTypeFromIndex(sIndex)}
                      dot={<CustomizedDot sIndex={sIndex} hoverLegend={hoverLegend} />}
                      activeDot={<CustomizedDot sIndex={sIndex} hoverLegend={hoverLegend} isOutline />}
                    />
                  ))}
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  width={500}
                  height={300}
                  data={chart?.data || []}
                  margin={{
                    top: 5,
                    right: 220,
                    left: 20,
                    bottom: 0
                  }}
                  onClick={(gItem) => updateSearchDashboard(gItem)}
                  onMouseMove={(e) => {
                    if (e?.activeTooltipIndex !== undefined && this.state.isHighlight !== e?.activeTooltipIndex) {
                      this.setState({ isHighlight: e.activeTooltipIndex });
                    } else {
                      this.setState({
                        isHighlight: e?.activeLabel ? e?.activeLabel : undefined
                      });
                    }
                  }}
                  onMouseLeave={() => this.setState({ isHighlight: undefined })}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  {isAngleAxis !== true ? (
                    <XAxis
                      dataKey="name"
                      interval="preserveStartEnd"
                      onClick={(aItem) => updateSearchDashboard(aItem)}
                    />
                  ) : (
                    <XAxis
                      dataKey="name"
                      interval="preserveStartEnd"
                      height={40 + longestName * 4.5}
                      tick={<CustomizedAxisTick showAll={true} isHighlight={this.state.isHighlight} />}
                      onClick={(aItem) => updateSearchDashboard(aItem)}
                    />
                  )}
                  <YAxis domain={isDomainMin ? ["dataMin", "dataMax"] : undefined} tickFormatter={yAxisTickFormat} />
                  <Tooltip
                    position={{
                      x: nChart === undefined ? xPos + 390 : xPos + 76,
                      y: 24
                    }}
                    wrapperStyle={{
                      background: "white",
                      padding: "10px",
                      border: "1px solid rgba(0, 0, 0, 0.2)",
                      boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)"
                    }}
                    contentStyle={{
                      background: "white",
                      border: "none"
                    }}
                    content={<CustomTooltip />}
                    active={this.state.isHighlight !== undefined}
                  />
                  {isHideLegend !== true && (
                    <Legend
                      height={160}
                      width={200}
                      align={"center"}
                      verticalAlign={"bottom"}
                      onMouseMove={(legItem) => this.setState({ hoverLegend: legItem?.value })}
                      onMouseLeave={() => this.setState({ hoverLegend: undefined })}
                      formatter={renderColorfulLegendText}
                    />
                  )}
                  {lineKeys
                    .filter((lineKey, sIndex) => sIndex < 4)
                    .map((lineKey, sIndex) => (
                      <Line
                        key={"line_" + sIndex}
                        type={chartOptions?.type === "Spline" ? "monotone" : "linear"}
                        dataKey={lineKey}
                        stroke={
                          hoverLegend === undefined || hoverLegend === lineKey
                            ? chart?.colors[sIndex]
                            : rgb(chart?.colors[sIndex]).copy({ opacity: 0.6 })
                        }
                        strokeWidth={hoverLegend === undefined ? 2 : hoverLegend === lineKey ? 3 : 1}
                        legendType={this.getLegendTypeFromIndex(sIndex)}
                        dot={<CustomizedDot sIndex={sIndex} hoverLegend={hoverLegend} />}
                        activeDot={<CustomizedDot sIndex={sIndex} hoverLegend={hoverLegend} isOutline />}
                      />
                    ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          {nChart && (
            <div className={styles.innerDChart}>
              <NextChartContainer
                id={id}
                // TODO: Madness please fix this. To show muscle chart
                node={{
                  chart: {
                    chart: nChart,
                    fields: nChart.fields,
                    narrative: nChart?.narrative
                  },
                  fields: nChart.fields
                }}
                chart={{ ...nChart, chart: nChart.chart }}
                isSmall={true}
                showTooltip={true}
                showLeakage={true}
                showAxis={true}
                isDashboard={true}
                hidePin={true}
                hideLegend={true}
                showTitle={true}
                isExpandChart={this.state.expandChart !== undefined}
                updateIsExpandChart={(rChart) => this.setState({ expandChart: rChart })}
              />
              <div className={styles.expandButton}>
                <Button
                  style={{ padding: "3px" }}
                  onClick={() =>
                    this.setState({
                      secondaryChartIndex: secondaryChartIndex - 1
                    })
                  }
                  disabled={secondaryChartIndex <= 0}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Button>
                <div className={styles.expandMiddle}>
                  {secondaryChartIndex + 1 + " / " + chart?.secondaryCharts?.length}
                </div>

                <Button
                  style={{ padding: "3px" }}
                  onClick={() =>
                    this.setState({
                      secondaryChartIndex: secondaryChartIndex + 1
                    })
                  }
                  disabled={secondaryChartIndex >= chart?.secondaryCharts?.length - 1}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  renderExpandModal() {
    const { id } = this.props;
    const { expandChart } = this.state;

    return (
      <Dialog open={expandChart !== undefined} onOpenChange={() => this.setState({ expandChart: undefined })}>
        <DialogContent size="full">
          <DialogHeader>
            <DialogTitle>{expandChart?.narrative?.title}</DialogTitle>
          </DialogHeader>
          <div>
            {expandChart && (
              <NextChartContainer
                id={id}
                // TODO: Madness please fix this. To show muscle chart
                node={{
                  chart: {
                    chart: expandChart,
                    fields: expandChart?.fields,
                    narrative: expandChart?.narrative
                  },
                  fields: expandChart?.fields
                }}
                chart={{ ...expandChart, chart: expandChart?.chart }}
                hideTitle
                hidePin
                isExpand
                showLeakage={true}
                showTooltip={true}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
}

class CustomizedAxisTick extends PureComponent {
  render() {
    const { x, y, stroke, payload, isHighlight, showAll } = this.props;

    const isHighlightTick = isHighlight === payload?.index;

    const isHideTick = false;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={6}
          dx={-3}
          opacity={isHideTick === true ? 0.4 : 1}
          textAnchor="end"
          fontWeight={isHighlightTick ? "bold" : undefined}
          fill={isHighlightTick ? "#512f77" : "#666"}
          transform="rotate(-45)"
        >
          {payload.value}
        </text>
      </g>
    );
  }
}

const EditChartButton = ({ updateDashboardChartOptions, dashboardChartOptions, chartTitleKey, chartOptions }) => {
  return (
    <div className={styles.editChartButtonWrapper}>
      <div id={"tooltipPosition_" + chartTitleKey} />
      <Dropdown className={styles.editChartContainer}>
        <Dropdown.Toggle className={styles.editChartButton}>
          <img src={EditChartIcon} style={{ width: "20px", marginRight: "6px" }} />
          {"Edit"}
        </Dropdown.Toggle>
        <Dropdown.Menu className={styles.editChartMenu}>
          {chartOptions.map((option) => {
            return (
              <MenuItem
                onClick={() => {
                  updateDashboardChartOptions({
                    ...dashboardChartOptions,
                    [chartTitleKey]: {
                      ...dashboardChartOptions?.[chartTitleKey],
                      type: option.value
                    }
                  });
                }}
                className={styles.editChartOption}
                key={chartTitleKey + "__" + option.value}
              >
                {option.label}
              </MenuItem>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

const CustomEllipsisLabel = ({ x, y, payload, yAxis, startChars, endChars, bufferChars }) => {
  const startLength = (startChars ?? yAxis) ? 6 : 10;
  const endLength = (endChars ?? yAxis) ? 6 : 8;
  const extraBufferChars = bufferChars ?? 4;

  let retText = payload?.value;

  if (payload?.value !== undefined && payload?.value?.length > startLength + endLength + extraBufferChars) {
    const startString = payload?.value?.slice(0, startLength) ?? "";
    const endString = payload?.value?.slice(-endLength) ?? "";

    retText = `${startString}...${endString}`;
  }

  return (
    <text x={x} y={y} textAnchor={yAxis ? "end" : "middle"} orientation={yAxis ? "left" : "bottom"}>
      <tspan dx={yAxis ? "-2px" : undefined} dy={yAxis ? "4px" : "10px"}>
        {retText ?? "Missing Value"}
      </tspan>
    </text>
  );
};
